// src/components/scratch/ScratchComingSoonModal.jsx
//
// "Coming Soon" promotional modal for the Scratch-Off Intelligence feature.
// Also doubles as the home-page launch teaser — same message, same gate.
//
// Visibility logic (non-overwhelming):
//   • Shows at most once every 7 days (localStorage key "scratch_cs_last_shown"
//     holds the timestamp of the last time it was shown/dismissed).
//   • Any dismissal — the × button, "Maybe later", or a successful mailing-list
//     sign-up — resets that timestamp, so the 7-day window restarts from
//     whichever page (Home or the Scratch dashboard) last showed it.
//   • First-ever visit (no stored timestamp) always shows it.
//
import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/AxiosInstance";

const STORAGE_KEY = "scratch_cs_last_shown";
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Hard-coded palette so the modal looks identical in light AND dark theme.
// The entire card sits on the brand nile-blue (#23364C), so we never rely
// on --surf / --txt / --txt-2 (which flip between themes).
const C = {
  bg:          "#23364C",
  bgDeep:      "#1B2A3D",
  border:      "rgba(255,255,255,0.10)",
  accentGreen: "#2EE56B",
  accentDim:   "rgba(46,229,107,0.14)",
  accentBorder:"rgba(46,229,107,0.30)",
  white:       "#FFFFFF",
  white78:     "rgba(255,255,255,0.78)",
  white55:     "rgba(255,255,255,0.55)",
  white12:     "rgba(255,255,255,0.12)",
  white20:     "rgba(255,255,255,0.20)",
  divider:     "rgba(255,255,255,0.12)",
  red:         "#FF8A80",
};

function readLastShown() {
  try {
    return Number(localStorage.getItem(STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
}

function markSeenNow() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // localStorage unavailable (private browsing, storage disabled, etc.) —
    // the modal will just show again next time; not worth failing loudly.
  }
}

export default function ScratchComingSoonModal({ onClose }) {
  const [visible, setVisible]     = useState(false);
  const [closing, setClosing]     = useState(false);
  const [email, setEmail]         = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg]   = useState("");

  useEffect(() => {
    if (Date.now() - readLastShown() > WEEK_MS) setVisible(true);
  }, []);

  const dismiss = () => {
    setClosing(true);
    markSeenNow();
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
      if (typeof onClose === "function") onClose();
    }, 300);
  };

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      setErrorMsg("Enter a valid email address.");
      return;
    }
    setErrorMsg("");
    setSubmitting(true);
    try {
      await axiosInstance.post(
        "/site/contact-message/add-to-mailing-list",
        { email: trimmed },
        { headers: { "Content-Type": "application/json" } }
      );
      setSubmitted(true);
      markSeenNow();
    } catch (err) {
      const status = err?.response?.status;
      setErrorMsg(
        status >= 500
          ? "Our system isn't reachable right now. Try again in a moment."
          : "Couldn't add you to the list. Check the email and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      onClick={dismiss}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(4px)",
        padding: "1rem",
        opacity: closing ? 0 : 1,
        transition: "opacity 0.3s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: "1.25rem",
          boxShadow: "0 32px 80px rgba(0,0,0,0.45)",
          width: "100%",
          maxWidth: 460,
          overflow: "hidden",
          transform: closing ? "scale(0.96)" : "scale(1)",
          transition: "transform 0.3s ease, opacity 0.3s ease",
          opacity: closing ? 0 : 1,
        }}
      >
        {/* ── Top accent bar ── */}
        <div style={{
          height: 3,
          background: `linear-gradient(90deg, transparent, ${C.accentGreen}, transparent)`,
        }} />

        {/* ── Header ── */}
        <div style={{
          background: C.bgDeep,
          padding: "1.75rem 2rem 1.5rem",
          textAlign: "center",
          borderBottom: `1px solid ${C.border}`,
        }}>
          {/* Ticket icon */}
          <div style={{ marginBottom: "1rem" }}>
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 56,
              height: 56,
              borderRadius: 16,
              background: C.accentDim,
              border: `1px solid ${C.accentBorder}`,
            }}>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path
                  d="M3 9a1 1 0 0 1 1-1h20a1 1 0 0 1 1 1v3.5a3 3 0 0 0 0 6V22a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3.5a3 3 0 0 0 0-6V9Z"
                  stroke={C.accentGreen} strokeWidth="1.6" strokeLinejoin="round" fill="none"
                />
                <path d="M10 10.5v1.5M10 14v1.5M10 17.5V19M13 10.5h7M13 14h7M13 17.5h5"
                  stroke={C.accentGreen} strokeWidth="1.4" strokeLinecap="round"
                />
              </svg>
            </div>
          </div>

          {/* Coming Soon pill */}
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            background: C.accentDim,
            border: `1px solid ${C.accentBorder}`,
            borderRadius: 999,
            padding: "0.2rem 0.75rem",
            marginBottom: "0.65rem",
          }}>
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: C.accentGreen,
            }}>
              Coming Soon
            </span>
          </div>

          <h2 style={{
            color: C.white,
            fontSize: "1.4rem",
            fontWeight: 700,
            lineHeight: 1.2,
            margin: "0 0 0.5rem",
          }}>
            Scratch-Off Intelligence
          </h2>
          <p style={{
            color: C.white78,
            fontSize: "0.875rem",
            lineHeight: 1.55,
            margin: 0,
          }}>
            A smarter way to see which scratch-off games still have
            the best prizes remaining.
          </p>
        </div>

        {/* ── Feature list ── */}
        <div style={{ padding: "1.5rem 2rem 0" }}>
          {[
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 2L11.09 6.26L16 7.27L12.5 10.67L13.18 15.59L9 13.36L4.82 15.59L5.5 10.67L2 7.27L6.91 6.26L9 2Z"
                    stroke={C.accentGreen} strokeWidth="1.5" strokeLinejoin="round" fill="none" />
                </svg>
              ),
              title: "Game Rankings by State",
              desc:  "TX, NY & FL games ranked by remaining prize inventory.",
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M2 13l4-4 3 3 4-5 3 3" stroke={C.accentGreen} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <path d="M2 4h14" stroke={C.accentGreen} strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              ),
              title: "Score Trends Over Time",
              desc:  "Track how a game's value shifts as prizes are claimed.",
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <rect x="2" y="3" width="14" height="12" rx="2" stroke={C.accentGreen} strokeWidth="1.5" fill="none" />
                  <path d="M5 7h8M5 10h5" stroke={C.accentGreen} strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              ),
              title: "Risk & Value Reports",
              desc:  "Detailed prize-tier breakdowns — available for Elite members.",
            },
          ].map(({ icon, title, desc }) => (
            <div key={title} style={{
              display: "flex",
              gap: "0.85rem",
              alignItems: "flex-start",
              marginBottom: "1rem",
            }}>
              <div style={{
                flexShrink: 0,
                width: 36,
                height: 36,
                borderRadius: 10,
                background: C.accentDim,
                border: `1px solid ${C.accentBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                {icon}
              </div>
              <div>
                <div style={{ fontSize: "0.85rem", fontWeight: 600, color: C.white, marginBottom: 2 }}>
                  {title}
                </div>
                <div style={{ fontSize: "0.78rem", color: C.white55, lineHeight: 1.45 }}>
                  {desc}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: "1rem 2rem 1.5rem",
          borderTop: `1px solid ${C.divider}`,
          marginTop: "0.5rem",
        }}>
          {submitted ? (
            <div style={{ textAlign: "center" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                marginBottom: 10,
                color: C.accentGreen,
                fontSize: "0.9rem",
                fontWeight: 700,
              }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle cx="9" cy="9" r="8" stroke={C.accentGreen} strokeWidth="1.4" />
                  <path d="M5.5 9.2l2.3 2.3L12.7 6.6" stroke={C.accentGreen} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
                You're on the list
              </div>
              <p style={{ color: C.white78, fontSize: "0.8rem", lineHeight: 1.5, marginBottom: "1.1rem" }}>
                We'll email <strong style={{ color: C.white }}>{email.trim()}</strong> the moment
                Scratch-Off Intelligence launches.
              </p>
              <button
                onClick={dismiss}
                style={{
                  width: "100%",
                  background: C.accentGreen,
                  color: "#23364C",
                  border: "none",
                  borderRadius: 999,
                  padding: "0.75rem 1rem",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  letterSpacing: "0.01em",
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <div style={{
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: C.white55,
                marginBottom: "0.6rem",
              }}>
                Get notified at launch
              </div>

              <style>{`
                .scratch-cs-email::placeholder { color: rgba(255,255,255,0.45); }
              `}</style>

              <form onSubmit={handleNotifySubmit} style={{ display: "flex", gap: 8 }}>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  className="scratch-cs-email"
                  value={email}
                  disabled={submitting}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errorMsg) setErrorMsg("");
                  }}
                  placeholder="you@example.com"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: "rgba(255,255,255,0.08)",
                    border: `1px solid ${C.white20}`,
                    borderRadius: 10,
                    padding: "0.65rem 0.85rem",
                    fontSize: "0.85rem",
                    color: C.white,
                    outline: "none",
                  }}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    flexShrink: 0,
                    background: C.accentGreen,
                    color: "#23364C",
                    border: "none",
                    borderRadius: 10,
                    padding: "0.65rem 1.1rem",
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    cursor: submitting ? "default" : "pointer",
                    opacity: submitting ? 0.7 : 1,
                    whiteSpace: "nowrap",
                    transition: "opacity 0.15s",
                  }}
                >
                  {submitting ? "Sending…" : "Notify me"}
                </button>
              </form>

              {errorMsg && (
                <p style={{ color: C.red, fontSize: "0.72rem", marginTop: 8 }}>
                  {errorMsg}
                </p>
              )}

              {/* Responsible gambling disclaimer */}
              <p style={{
                fontSize: "0.7rem",
                color: C.white55,
                textAlign: "center",
                lineHeight: 1.5,
                margin: "1.1rem 0 0.75rem",
              }}>
                For entertainment and informational purposes only. This feature does
                not predict winning tickets. Please play responsibly.
              </p>

              <button
                onClick={dismiss}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  color: C.white55,
                  fontSize: "0.78rem",
                  cursor: "pointer",
                  padding: "0.25rem",
                  textDecoration: "underline",
                }}
              >
                Maybe later
              </button>
            </>
          )}
        </div>

        {/* ── Close ✕ ── */}
        <button
          onClick={dismiss}
          aria-label="Close"
          style={{
            position: "absolute",
            top: "0.85rem",
            right: "0.85rem",
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: C.white12,
            border: `1px solid ${C.white20}`,
            color: C.white78,
            fontSize: "1.1rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = C.white20)}
          onMouseLeave={(e) => (e.currentTarget.style.background = C.white12)}
        >
          ×
        </button>
      </div>
    </div>
  );
}
